import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Send, Trash2, Bot, Calendar, MessageSquare, Activity,
  Pill, FileText, Heart, HelpCircle,
} from 'lucide-react';
import { useAIAssistantStore } from '../stores/ai-assistant-store';
import { handleAIAction } from '../utils/ai-action-handler';
import { AIMessageBubble } from '../components/ai-assistant/AIMessageBubble';
import { AISuggestionChips } from '../components/ai-assistant/AISuggestionChips';
import type { AIAction } from '../types/ai-assistant-types';

const SUGGESTED_TOPICS = [
  { icon: Activity, label: 'Explain my test results', color: 'text-blue-500' },
  { icon: Calendar, label: 'Help me book an appointment', color: 'text-green-500' },
  { icon: Pill, label: 'Medication information', color: 'text-purple-500' },
  { icon: FileText, label: 'Summarize my health records', color: 'text-orange-500' },
  { icon: Heart, label: 'Wellness & prevention tips', color: 'text-red-500' },
  { icon: HelpCircle, label: 'How to use KaiMed', color: 'text-indigo-500' },
];

const QUICK_ACTIONS = [
  { icon: Calendar, label: 'Schedule Appointment', path: '/schedule-appointment' },
  { icon: MessageSquare, label: 'Message Doctor', path: '/messages' },
  { icon: Activity, label: 'View Test Results', path: '/test-results' },
];

export default function AIAssistant() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    messages,
    isLoading,
    sendMessage,
    clearConversation,
    setFullPage,
    retryLastMessage,
  } = useAIAssistantStore();

  useEffect(() => {
    setFullPage(true);
    return () => setFullPage(false);
  }, [setFullPage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput('');
    await sendMessage(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleActionClick = (action: AIAction) => {
    handleAIAction(action, navigate);
  };

  const handleTopicClick = (topic: string) => {
    sendMessage(topic);
  };

  const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
  const currentSuggestions = lastAssistantMsg?.suggestions || [];

  return (
    <div className="h-full flex gap-6 max-w-7xl mx-auto px-4 py-6">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-white/70 dark:bg-slate-800/60 backdrop-blur-md border border-[var(--border-subtle)] rounded-2xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)]
          bg-[var(--brand-primary)] text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-semibold text-lg">KaiMed AI Assistant</h1>
              <p className="text-xs text-white/80">Your personal health guide</p>
            </div>
          </div>
          <button
            onClick={clearConversation}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/20 transition-colors text-sm"
            title="Clear conversation"
          >
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">Clear</span>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {messages.map((message) => (
            <AIMessageBubble
              key={message.id}
              message={message}
              onActionClick={handleActionClick}
              onRetry={message.error ? retryLastMessage : undefined}
            />
          ))}

          {isLoading && (
            <div className="flex gap-2.5">
              <div className="w-8 h-8 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-gray-100 dark:bg-slate-800/80 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {!isLoading && currentSuggestions.length > 0 && (
          <AISuggestionChips suggestions={currentSuggestions} onSelect={handleTopicClick} disabled={isLoading} />
        )}

        {/* Input Bar */}
        <div className="border-t border-[var(--border-subtle)] px-4 py-3 bg-white/50 dark:bg-slate-900/50 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything about your health, appointments, or medications..."
              disabled={isLoading}
              className="flex-1 bg-white dark:bg-slate-800/80 rounded-full px-5 py-3 text-sm
                text-[var(--text-primary)] placeholder-[var(--text-tertiary)]
                border border-[var(--border-subtle)] focus:border-[var(--brand-primary)]
                focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-opacity-20
                disabled:opacity-50 transition-all shadow-sm"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="flex-shrink-0 w-11 h-11 rounded-full
                bg-[var(--brand-primary)] text-white
                flex items-center justify-center
                hover:bg-[var(--brand-primary-hover)]
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all shadow-md hover:shadow-lg"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-[10px] text-gray-400 text-center mt-2">
            AI responses are for informational purposes only. Always consult your healthcare provider for medical advice.
          </p>
        </div>
      </div>

      {/* Sidebar Panel */}
      <div className="hidden lg:flex w-80 flex-col gap-4">
        <div className="bg-white/70 dark:bg-slate-800/60 backdrop-blur-md border border-[var(--border-subtle)] rounded-2xl shadow-sm p-5">
          <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Suggested Topics</h2>
          <div className="space-y-2">
            {SUGGESTED_TOPICS.map((topic, index) => (
              <button
                key={index}
                onClick={() => handleTopicClick(topic.label)}
                disabled={isLoading}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                  text-sm text-gray-600 dark:text-gray-300
                  hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100
                  disabled:opacity-50 transition-colors text-left"
              >
                <topic.icon className={`w-4 h-4 flex-shrink-0 ${topic.color}`} />
                <span>{topic.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white/70 dark:bg-slate-800/60 backdrop-blur-md border border-[var(--border-subtle)] rounded-2xl shadow-sm p-5">
          <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Quick Actions</h2>
          <div className="space-y-2">
            {QUICK_ACTIONS.map((action, index) => (
              <Link
                key={index}
                to={action.path}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl
                  text-sm text-gray-600 dark:text-gray-300
                  hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400
                  transition-colors"
              >
                <action.icon className="w-4 h-4 flex-shrink-0" />
                <span>{action.label}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-4">
          <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
            <strong>Medical Disclaimer:</strong> This AI assistant provides general health information only.
            It does not replace professional medical advice, diagnosis, or treatment. Always seek the advice
            of your physician or other qualified health provider.
          </p>
        </div>
      </div>
    </div>
  );
}
